/**
 * @fileoverview 认证相关 API 服务
 * @description 封装 EVM 钱包登录、Telegram 登录等认证接口
 * MVC 架构：Service 层，负责认证相关 HTTP 通信
 * @module services/authService
 */

import { post, type BackendResponse } from './api';

// ============================================================
// 类型定义
// ============================================================

/**
 * EVM 钱包登录请求参数
 */
export interface EvmLoginRequest {
  /** 链类型，固定值 "evm" */
  chain: 'evm';
  /** 钱包地址 */
  address: string;
  /** 签名数据 */
  sign: string;
  /** 链 ID（如 1=ETH主网, 56=BSC, 137=Polygon） */
  chain_id: number;
  /** 公钥（EVM 链不需要，可选） */
  publicKey?: string;
}

/**
 * Telegram Mini App 登录请求参数
 */
export interface TelegramLoginRequest {
  /** Telegram WebApp.initData（原始字符串，后端用于验证） */
  initData: string;
  /** 邀请码（可选） */
  refCode?: string;
  /** 邀请类型（可选） */
  refType?: string;
}

/**
 * 登录成功返回的用户数据
 */
export interface LoginResponseData {
  /** 钱包地址（主键） */
  account: string;
  /** 用户 GUID */
  guid: number;
  /** 链类型 */
  chain: string;
  /** 账户名称 */
  accountName: string;
  /** 推荐码 */
  referralCode?: string;
  /** JWT Token（格式：`"Bearer <jwt>"`） */
  token: string;
  /** 是否首次登录 */
  isFirstLogin?: boolean;
  /** 余额 */
  balance?: number;
  /** 邮箱（可选） */
  email?: string;
}

// ============================================================
// API 函数
// ============================================================

/**
 * EVM 钱包签名登录
 * @description 后端验证签名逻辑：
 *   1. 构造消息：`"address=<checksumAddress>,chain_id=<chainId>"`
 *   2. 用户用钱包对该消息进行 `personal_sign`（eth_sign）
 *   3. 将签名发送给后端验证
 * @param params - 登录参数
 * @returns 登录响应（含 token 和用户信息）
 */
export async function evmLogin(
  params: EvmLoginRequest
): Promise<BackendResponse<LoginResponseData>> {
  return post<LoginResponseData>('/api/v1/evmLogin', params);
}

/**
 * Telegram Mini App 登录
 * @description 后端验证流程：
 *   1. 接收前端传来的 initData（Telegram WebApp.initData）
 *   2. 用 HMAC-SHA256 验证 initData 的合法性（防伪造）
 *   3. 解析 user 字段，用 tg_id 作为账户标识注册/登录
 *   4. 返回 JWT token
 * @param params - TG 登录参数
 * @returns 登录响应（含 token 和用户信息）
 */
export async function telegramLogin(
  params: TelegramLoginRequest
): Promise<BackendResponse<LoginResponseData>> {
  return post<LoginResponseData>('/api/v1/tonLogin/', params);
}
